import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronLeft, Globe, ShieldCheck, Loader2, AlertTriangle } from "lucide-react";

export default function SystemControl() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user.role !== "super_admin") {
      navigate("/admin");
      return;
    }
    api.get("/system").then((r) => setS(r.data)).catch(() => toast.error("Failed to load"));
  }, [user, navigate]);

  if (user && user.role !== "super_admin") return null;
  if (!s) return <div className="min-h-screen bg-[#0A0A0A] text-white/60 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const toggle = async (key) => {
    const next = { ...s, [key]: !s[key] };
    // Safety: never let super_admin lock themselves out of /admin/login
    setSaving(true);
    try {
      const { data } = await api.put("/system", { [key]: !s[key] });
      setS(data);
      toast.success(next[key] ? "Activado" : "Desactivado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-body p-6 sm:p-10">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-[#D4AF37] mb-5">
          <ChevronLeft className="w-3.5 h-3.5" /> Volver al hub
        </button>

        <div className="flex items-center gap-3 mb-2">
          <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">Super Admin</p>
          <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest bg-red-500/15 text-red-300 border border-red-500/30 font-bold">
            Acceso total
          </span>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">Control de sistema</h1>
        <p className="text-white/60 text-sm sm:text-base mb-8">
          Activa o desactiva los componentes principales del proyecto. Solo el super admin puede acceder aquí.
        </p>

        <div className="space-y-4">
          <Toggle
            icon={Globe}
            title="Landing page público"
            description="Cuando está desactivado, los visitantes de mccleanfresh.com ven un mensaje de mantenimiento."
            value={s.landing_enabled}
            onChange={() => toggle("landing_enabled")}
            saving={saving}
            testid="toggle-landing"
          />
          <Toggle
            icon={ShieldCheck}
            title="Panel de administración"
            description="Cuando está desactivado, los admins normales no pueden iniciar sesión. Tú (super admin) siempre tendrás acceso."
            value={s.admin_enabled}
            onChange={() => toggle("admin_enabled")}
            saving={saving}
            testid="toggle-admin"
          />
        </div>

        <div className="mt-10 p-4 bg-amber-500/10 border border-amber-500/30 rounded-2xl flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-200/90 leading-relaxed">
            Los cambios son inmediatos en preview. En producción ({"mccleanfresh.com"}), también son inmediatos porque ambos entornos comparten la API/DB del backend. <strong>Recuerda redeployar</strong> sólo si modificas código.
          </div>
        </div>
      </div>
    </div>
  );
}

function Toggle({ icon: Icon, title, description, value, onChange, saving, testid }) {
  return (
    <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-5 sm:p-6 flex items-start gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border ${value ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-white/5 border-white/15 text-white/40"}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-display text-lg font-bold">{title}</h3>
          <button
            onClick={onChange}
            disabled={saving}
            data-testid={testid}
            className={`relative w-12 h-7 rounded-full transition-all border ${value ? "bg-[#D4AF37] border-[#D4AF37]" : "bg-white/10 border-white/15"} disabled:opacity-60`}
            aria-pressed={value}
          >
            <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-all ${value ? "left-[22px]" : "left-0.5"}`} />
          </button>
        </div>
        <p className="text-sm text-white/55 mt-1.5">{description}</p>
        <div className="mt-3 text-[11px] uppercase tracking-widest font-bold">
          {value ? <span className="text-emerald-300">Activado</span> : <span className="text-white/40">Desactivado</span>}
        </div>
      </div>
    </div>
  );
}
